<template>
  <v-dialog 
    v-model="dialogBooking" 
    class="bookingDialog"
    :fullscreen="$vuetify.display.mdAndDown" 
    transition="dialog-top-transition"
    :scroll-strategy="'none'"
    @after-leave="$emit('close')"
    >

    <v-card flat class="bookingCard">

      <!-- Favoriten-Knopf und Exit -->
      <v-row class="align-center justify-space-between mt-n2 mr-1">
        <v-col cols="auto">
          <FavoriteButton 
            :favBtnIsFavorite="bookingDialogSeat?.isFavorite"
            :favBtnSeatID="bookingDialogSeat?.id"
            :favBtnFavoriteID="bookingDialogSeat?.favoriteID"
            @updateFavorite="$emit('updateFavorite')"/>
        </v-col>
        <v-col cols="auto">
          <ExitButton @click="closeDialog" />
        </v-col>
      </v-row>

      <!-- Titel und Subtitel -->
      <v-row dense class="justify-center align-center mt-n8">
        <v-col cols="auto">
          <v-card-title>{{ bookingDialogSeat?.description }}</v-card-title>
        </v-col>
      </v-row>
      <v-row dense class="justify-center align-center mt-n4">
        <v-col cols="auto">
          <v-card-subtitle>
            {{ bookingDialogSeat?.room + ', Platz '+ bookingDialogSeat?.name }}
          </v-card-subtitle>
        </v-col>
      </v-row>

      <!-- Benutzer -->
      <v-row class="align-center justify-center">
        <v-col cols="auto">
          <DropDownEmployees 
            v-model="selectedUserID"
            :dropDownEmpUsers="bookingDialogUsers"/>
        </v-col>
      </v-row>

      <v-row class="justify-center align-stretch ma-4">
        <!-- DatePicker -->
        <v-col cols="6">
          <v-card flat class="datePickerCard h-100">
            <v-date-picker
              v-model="selectedDate"
              color="#FF7500"
              locale="de-DE"
              first-day-of-week="1"
              show-adjacent-months
              multiple
              :allowed-dates="isDateAllowed"
            />
          </v-card>
        </v-col>

        <!-- Textarea -->
        <v-col cols="6" class="d-flex flex-column">
          <v-textarea
            v-model="bookingCardComment"
            label="Kommentar"
            prepend-inner-icon="mdi-message"
            variant="solo"
            clearable
            auto-grow="false"
            class="fill-height commentaryField"
          />
        </v-col>
      </v-row>

      <!-- Aktionen -->
      <v-row dense class="justify-end align-center mr-4 mt-n10 mb-2">
        <v-card-actions>
          <v-btn 
            variant="elevated" 
            color="primary"
            prepend-icon="mdi-check-circle"  
            @click="confirmBookingSeat">
            Platz buchen
          </v-btn>
          <v-btn 
            variant="elevated" 
            color="grey" 
            @click="closeDialog">
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-row>
    </v-card>
  </v-dialog>

  <SnackbarBooking ref="snackbarBookingRef" />
</template>

<script>
import ExitButton from '../consumables/ExitButton.vue'
import FavoriteButton from '../consumables/FavoriteButton.vue'
import DropDownEmployees from '../consumables/DropDownEmployees.vue'
import SnackbarBooking from '../consumables/SnackbarBooking.vue'

export default {
  name: 'BookingDialog',
  emits: ['updateFavorite', 'updateSeatStatus', 'close'],

  components: {
    ExitButton,
    FavoriteButton,
    DropDownEmployees,
    SnackbarBooking,
  },

  props: {
    bookingDialogStartDate: { type: String, required: true },
    bookingDialogEndDate: { type: String, required: true },
    bookingDialogSeat: { type: Object, default: null },
    bookingDialogUsers: {type: Object, default: null } 
  },

  data() {
    return {
      dialogBooking: false,
      selectedDate: [],
      bookedDates: [],
      releaseDates: [],
      isFavorite: false,
      selectedUserID: null,
      bookingCardComment: ''
    }
  },

  methods: {
    async openDialogBooking() {
      const isOwnerSeat = !!this.bookingDialogSeat?.owner
      console.log('OwnerSeat?', isOwnerSeat)
      this.isFavorite = this.bookingDialogSeat.isFavorite
      this.bookingCardComment = ''
      await this.getBookedDates()
      await this.getReleaseDates()

      const toBookDates = this.generateMarkedDates(
        this.bookingDialogStartDate,
        this.bookingDialogEndDate
      )

      if (isOwnerSeat) {
        // OwnerSeat → nur explizit freigegebene Tage
        this.selectedDate = toBookDates.filter(
          date =>
            !this.bookedDates.includes(date) &&
            this.releaseDates.includes(date)
        )
      } else {
        // normaler Sitz
        this.selectedDate = toBookDates.filter(
          date => !this.bookedDates.includes(date)
        )
      }
      this.dialogBooking = true
    },

    closeDialog(){
      this.dialogBooking = false
    },

    async getBookedDates() {
      if (!this.bookingDialogSeat?.id) return
      try {
        const response = await this.$axios.get(`/api/seats/${this.bookingDialogSeat.id}/booked-dates/`)
        this.bookedDates = response.data
      } catch (error) {
        console.error("Fehler beim Laden der gebuchten Daten:", error)
      }
    },

    async getReleaseDates() {
      this.releaseDates = []
      if (!this.bookingDialogSeat?.id) return
      if (!this.bookingDialogSeat.owner) return

      try {
        const response = await this.$axios.get(`/api/owner-release-items/`, {
          params: {seat: this.bookingDialogSeat.id}})
        const dataArray = response.data
        this.releaseDates = dataArray.map(item => item.date)

      }catch(err){
        console.error('Fehler beim Laden der Freigaben im Buchungsvorgang', err)
      }
    },

    generateMarkedDates(start, end) {
      const result = []
      const current = new Date(start)
      const to = new Date(end)

      while (current <= to) {
        result.push(current.toISOString().split('T')[0])
        current.setDate(current.getDate() + 1)
      }
      return result
    },

    async confirmBookingSeat() {
      const isOwnerSeat = !!this.bookingDialogSeat?.owner

      if (isOwnerSeat && this.releaseDates.length === 0) {
        console.error('OwnerSeat ohne Freigabe - Buchung blockiert')
        return
      }

      if (!this.bookingDialogSeat) {
        console.error("Kein Sitz ausgewählt!")
        return
      }

      if (!this.selectedUserID) {
        console.error("Kein User ausgewählt!")
        return
      }

      if (this.selectedDate.length === 0) {
        console.error("Keine Tage ausgewählt!")
        return
      }

      const reservationItems = this.selectedDate.map(date => ({
        start: this.formatDateToYMD(date),
        end: this.formatDateToYMD(date),
      }))

      const postData = {
        seat_id: this.bookingDialogSeat.id,
        comment: this.bookingCardComment,
        reserved_for: this.selectedUserID,
        reservation_items: reservationItems,
      }

      try {
        await this.$axios.post('/api/reservations/', postData)
        this.dialogBooking = false
        this.$refs.snackbarBookingRef.show()
        this.$emit('updateSeatStatus') // Parent informieren
      } catch (error) {
        console.error("Fehler beim Erstellen der Reservierungen:", error)
      }
    },

    isDateAllowed(date) {
      const day = new Date(date).getDay();

      if (day === 0 || day === 6) return false;

      const maxDate = new Date();
      maxDate.setMonth(maxDate.getMonth() + 3);

      if (new Date(date) > maxDate) return false;

      const formattedDate = this.formatDateToYMD(date)
      const today = this.formatDateToYMD(new Date());
      
      const isOwnerSeat = !!this.bookingDialogSeat?.owner
      if (isOwnerSeat) {
        return (
          this.releaseDates.includes(formattedDate) &&
          !this.bookedDates.includes(formattedDate) &&
          formattedDate >= today
        )
      }

      return (
        !this.bookedDates.includes(formattedDate) &&
        formattedDate >= today
      )
    },

    formatDateToYMD(date) {
      if (typeof date === 'string') return date
      const newDate = new Date(date)
      const year = newDate.getFullYear()
      const month = String(newDate.getMonth() + 1).padStart(2, '0')
      const day = String(newDate.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
  }
}
</script>

<style scoped>
.bookingDialog {
  max-width: 49rem;
}
.bookingCard {
  scrollbar-width: none !important;
  -ms-overflow-style: none;
}
.datePickerCard {
  height: 29rem !important;
  box-shadow: 0px 6px 15px hsla(0, 0%, 0%, 0.5);
}
.commentaryField {
  height: 30.5rem !important;
  flex-direction: column;
}
@media (max-width: 600px) {
  .btn-responsive{
    font-size: 0.5rem;
  }
}

</style>
